import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store';
import { CursorType } from 'configs/constant';

export interface NotificationAlertType {
  id: string;
  type: string;
  timestamp: number;
  locationId: string;
  locationName: string;
  notificationText: string;
  messNotification:string;
}
export interface NotificationType {
  receiver: string;
  timestamp: number;
  title: string;
  message: string;
  location_id: string;
  gateway_serial: string;
  readed: boolean;
  type: 'alert' | 'warning';
  data: {
    nType: string;
    nId: string;
    timestamp: number;
    status: number;
    battery: number;
    sensors: [
      {
        mode: number,
        status: number,
        type: string
      }
    ]
  };
}

interface NotificationState {
  alertNotifications: NotificationType[];
  warningNotifications: NotificationType[];
  notificationsAlertQueue: NotificationAlertType[];
  cursor: {
    alert: CursorType;
    warning: CursorType;
  };
  limit: number;
}

const initialState: NotificationState = {
  alertNotifications: [],
  warningNotifications: [],
  notificationsAlertQueue: [],
  cursor: {
    alert: {},
    warning: {},
  },
  limit: 50,
};

const slice = createSlice({
  name: 'notificationState',
  initialState,
  reducers: {
    setNotifications: (
      state,
      action: PayloadAction<{ notifications: NotificationType[]; cursor: CursorType; type: 'alert' | 'warning' }>,
    ) => {
      if (action.payload.type === 'alert') {
        state.alertNotifications = action.payload.notifications;
        state.cursor.alert = action.payload.cursor;
      } else {
        state.warningNotifications = action.payload.notifications;
        state.cursor.warning = action.payload.cursor;
      }
    },
    addNotifications: (
      state,
      action: PayloadAction<{ notifications: NotificationType[]; cursor: CursorType; type: 'alert' | 'warning' }>,
    ) => {
      if (action.payload.type === 'alert') {
        state.alertNotifications = [...state.alertNotifications, ...action.payload.notifications];
        state.cursor.alert = action.payload.cursor;
      } else {
        state.warningNotifications = [...state.warningNotifications, ...action.payload.notifications];
        state.cursor.warning = action.payload.cursor;
      }
    },
    addNotificationsAlertQueue: (state, { payload: notificationAlert }: PayloadAction<NotificationAlertType>) => {
      const isInQueue = state.notificationsAlertQueue.find((item) => item.locationId === notificationAlert.locationId);
      const newState = isInQueue
        ? [...state.notificationsAlertQueue]
        : [...state.notificationsAlertQueue, notificationAlert];
      state.notificationsAlertQueue = [...newState];
    },
    removeNotificationAlertFromQueue: (state, { payload: notificationAlert }: PayloadAction<NotificationAlertType>) => {
      state.notificationsAlertQueue = state.notificationsAlertQueue.filter((item) => item.id !== notificationAlert.id);
    },
  },
});

export const { setNotifications, addNotifications, addNotificationsAlertQueue, removeNotificationAlertFromQueue } =
  slice.actions;

export default slice.reducer;

export const selectNotificationState = (state: RootState) => state.notificationState;
export const selectNotifications = (state: RootState, type: 'alert' | 'warning') =>
  type === 'alert' ? state.notificationState.alertNotifications : state.notificationState.warningNotifications;
export const selectNotificationsAlertQueue = (state: RootState) => state.notificationState.notificationsAlertQueue;
export const selectNotificationsCursor = (state: RootState, type: 'alert' | 'warning') =>
  type === 'alert' ? state.notificationState.cursor.alert : state.notificationState.cursor.warning;